import React, { useEffect, useState } from 'react';
import Slider from 'react-slick';
import { Link, useHistory } from 'react-router-dom';
import { useQuery } from 'react-apollo';

import { GET_BLOG_POSTS } from 'main/graphql';
import { LoadingSpinner } from 'main/components';

const sliderSettings = {
  className: 'center',
  dots: true,
  infinite: true,
  centerMode: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: true,
  autoplay: false,
  autoplaySpeed: 3000,
  centerPadding: '10%',
  responsive: [
    {
      breakpoint: 768,
      settings: {
        centerMode: false,
        arrows: false,
        centerPadding: '0px',
      },
    },
  ],
};

export const WhatsNew = () => {
  const history = useHistory();

  const [activeIndex, setActiveIndex] = useState(0);
  const [isTablet, setIsTablet] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [carouselItems, setCarouselItems] = useState<any[]>([]);

  const [retryCount, setRetryCount] = useState(0);

  const { loading, data, error, refetch } = useQuery(GET_BLOG_POSTS, {
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (error && retryCount < 2) {
      setRetryCount(prev => prev + 1);

      refetch().catch(() => {
        console.warn(`Retry ${retryCount + 1}: of news fetching failed.`);
      });
    }
  }, [error, retryCount, refetch]);

  useEffect(() => {
    if (data?.allBlog_posts?.edges) {
      const allFeatured = data.allBlog_posts.edges
        .filter((post: any) => post.node.featured_post === true)
        .sort((a: any, b: any) => {
          const dateA = new Date(
            a.node.published_date || a.node._meta?.firstPublicationDate || 0,
          ).getTime();
          const dateB = new Date(
            b.node.published_date || b.node._meta?.firstPublicationDate || 0,
          ).getTime();
          return dateB - dateA;
        });

      const getExplicitPosition = (position: number) => {
        return allFeatured.find(
          (post: any) => Number(post.node.featured_position) === position,
        );
      };

      let pos1 = getExplicitPosition(1);
      let pos2 = getExplicitPosition(2);
      let pos3 = getExplicitPosition(3);

      const assignedUids = [pos1, pos2, pos3]
        .filter(Boolean)
        .map((p: any) => p.node._meta.uid);

      const unassignedPosts = allFeatured.filter(
        (p: any) => !assignedUids.includes(p.node._meta.uid),
      );

      if (!pos1) pos1 = unassignedPosts.shift();
      if (!pos2) pos2 = unassignedPosts.shift();
      if (!pos3) pos3 = unassignedPosts.shift();

      const topPostsRaw = [pos1, pos2, pos3].filter(Boolean);

      const mappedPosts = topPostsRaw.map((post: any) => {
        const keywordText =
          typeof post.node.featured_keyword === 'string'
            ? post.node.featured_keyword
            : post.node.featured_keyword?.[0]?.text || 'Featured';

        const shortTitleText =
          post.node.featured_short_title?.[0]?.text ||
          post.node.title?.[0]?.text ||
          '';

        return {
          image: post.node.featured_image?.url || post.node.hero_image?.url,
          imageMobile:
            post.node.featured_image?.url || post.node.hero_image?.url,
          date: keywordText,
          desc: shortTitleText,
          link: `/news-details/${post.node._meta.uid}`,
        };
      });

      setCarouselItems(mappedPosts);
    }
  }, [data]);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsTablet(width < 992);
      setIsMobile(width <= 767);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const navigateToLink = (link: string) => {
    history.push(link);
  };

  if (loading || (error && retryCount < 2)) return <LoadingSpinner />;
  if (error && retryCount >= 2) return null;
  if (!loading && !error && carouselItems.length === 0) return null;

  return (
    <section className="newhomepage-new-section">
      <div className="newhomepage-new-upper-content">
        <h2 className="newhomepage-new-upper-title">See what’s new</h2>
        <div className="newhomepage-new-upper-btn">
          <Link
            to="/news"
            onMouseDown={event => event.preventDefault()}
            className="newhomepage-new-upper-anchor"
          >
            explore all
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <g clipPath="url(#clip0_5838_5663)">
                <path
                  d="M5.63405 18.3641L18.362 5.63614M18.362 5.63614V17.657M18.362 5.63614H6.34115"
                  stroke="#FF2238"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
              <defs>
                <clipPath id="clip0_5838_5663">
                  <rect width="24" height="24" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </Link>
        </div>
      </div>

      {isTablet ? (
        <div className="newhomepage-new-carosel-slider">
          <Slider {...sliderSettings}>
            {carouselItems.map((item, index) => (
              <div
                key={index}
                style={{ padding: '10px' }}
                onClick={() => navigateToLink(item.link)}
              >
                <div
                  className={`whatsnew-slider-slide ${
                    index <= 1
                      ? 'whatsnew-slider-slide-first'
                      : index === 2
                      ? 'whatsnew-slider-slide-last'
                      : ''
                  }`}
                  style={{
                    background: `${
                      index === 0 || index === 1 || index === 2
                        ? 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 35.84%, rgba(0, 0, 0, 0.80) 80.05%)'
                        : 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 35.84%, rgba(0, 0, 0, 0.00) 80.05%)'
                    }, url(${
                      isMobile ? item.imageMobile : item.image
                    }) lightgray 50% / cover no-repeat`,
                  }}
                >
                  <div className="whatsnew-slider-content">
                    <h3 className="whatsnew-slider-title">
                      {item.date}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <g clipPath="url(#clip0_5838_5663)">
                          <path
                            d="M5.63405 18.3641L18.362 5.63614M18.362 5.63614V17.657M18.362 5.63614H6.34115"
                            stroke="#ffffffff"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_5838_5663">
                            <rect width="24" height="24" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                    </h3>
                    <p className="whatsnew-slider-description">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </Slider>
        </div>
      ) : (
        <div className="newhomepage-new-carosel">
          {carouselItems.map((item, index) => (
            <div
              key={index}
              className={`newhomepage-new-carosel-container ${
                index === activeIndex ? 'is-active' : ''
              }`}
              onMouseOver={() => setActiveIndex(index)}
              onClick={() => navigateToLink(item.link)}
            >
              <div
                className="newhomepage-new-carosel-bg"
                style={{
                  background: `linear-gradient(
                    186deg,
                    rgba(0, 0, 0, ${index === activeIndex ? 0.1 : 0.5}) 29.41%,
                    rgba(0, 0, 0, ${index === activeIndex ? 0.1 : 0.5}) 95.83%
                  ),
                  url(${item.image}) lightgray center 30% / cover no-repeat`,
                  backgroundBlendMode: 'multiply, normal',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background:
                    'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 35.84%, rgba(0, 0, 0, 0.80) 80.05%)',
                  pointerEvents: 'none',
                  zIndex: 1,
                }}
              />

              <div className="newhomepage-new-carosel-text">
                <h3 className="newhomepage-new-carosel-title">{item.date} </h3>
                <p className="newhomepage-new-carosel-description">
                  {item.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
